import {Injectable} from '@angular/core';
import {ApiService} from './api.service';
import {CommonUtils} from '../utils/common';
import {BehaviorSubject, empty, Observable} from 'rxjs';
import {User} from '../models/user.model';
import {UserSignup} from '../models/user-signup.model';
import {UserSignupResponse} from '../models/user-signup-response.model';
import {VetineoApiResponse} from '../models/vetineo-api-response.model';
import {map, switchMap, take} from 'rxjs/operators';
import {UserSignin} from '../models/user-signin.model';
import {UserSigninResponse} from '../models/user-signin-response.model';
import {fromPromise} from 'rxjs/internal-compatibility';
import {CurrentBalanceResponse} from '../models/finance/current-balance-response.model';
import {Order} from '../models/shop/order.model';
import {UsersDgSearchResults} from "../models/datagrid-search-results/users.dg-search-results.model";
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {Snapshot} from "../models/snpashot.model";
import {VetClinic} from "../models/vet-clinic.model";
import {Pet} from "../models/pet.model";
import {SimpleCheckVisit} from "../models/simple-check-visit.model";

/**
 * Główny serwis domenowy zarządzający użytkownikami w systemie.
 * Odpowiada za procesy uwierzytelniania, zarządzanie profilem, uprawnieniami (Feature Toggles),
 * powiązaniami z klinikami/pacjentami oraz zarządzanie globalnym stanem zalogowanego użytkownika (State Management).
 */
@Injectable({
  providedIn: 'root'
})
export class UserService implements DataGridServiceInterface<Observable<UsersDgSearchResults>> {
  /**
   * Reaktywny strumień przechowujący globalny stan zalogowanego użytkownika.
   * Umożliwia asynchroniczne odświeżanie interfejsu w całej aplikacji w reakcji na zmianę profilu.
   */
  private user = new BehaviorSubject<User>(null);

  HAS_LOGGED_IN = 'hasLoggedIn';

  constructor(
    private apiService: ApiService,
    public commonUtils : CommonUtils,
  ) {
  }

  /**
   * Pobiera aktualny stan użytkownika z pamięci podręcznej RAM (BehaviorSubject).
   * Jeśli stan jest pusty, próbuje zrekonstruować sesję z lokalnej pamięci przeglądarki.
   *
   * @returns Observable emitujący obiekt zalogowanego użytkownika.
   */
  getUser() {
    if (this.user.getValue()) {
      return this.user.asObservable();
    } else {
      return fromPromise(this.commonUtils.getStorageObject<User>('user').then((userObj) => {
        return userObj;
      })).pipe(take(1), switchMap(user => {
        this.user.next(user);
        return this.user;
      }));
    }
  }

  /**
   * Ustawia nowy stan użytkownika po pomyślnym logowaniu lub edycji profilu,
   * rozgłaszając zmianę przez BehaviorSubject i zapisując dane trwale w pamięci urządzenia.
   *
   * @param user Obiekt zalogowanego użytkownika.
   */
  setUser(user: User) {
    this.user.next(user);
    this.storeUserData(user);
  }

  /**
   * Zapisuje obiekt użytkownika do trwałej pamięci lokalnej (Local Storage).
   */
  private storeUserData(user: User) {
    this.commonUtils.setStorageObject('user', user);
  }

  /**
   * Rejestruje nowe konto użytkownika w systemie.
   */
  register(userSignup: UserSignup): Observable<UserSignupResponse> {
    if (this.commonUtils.isEmpty(userSignup)) {
      return empty();
    }
    return this.apiService.post<VetineoApiResponse>('/dashboard/users', userSignup).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedCopyEver<UserSignupResponse>(new UserSignupResponse(), vetineoApiResponse.response);
      }
    }));
  }

  /**
   * Uwierzytelnia użytkownika na podstawie danych logowania (Login).
   */
  signIn(userSignin: UserSignin): Observable<UserSigninResponse> {
    if (this.commonUtils.isEmpty(userSignin)) {
      return empty();
    }
    // const options = { headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded') };

    return this.apiService.post<VetineoApiResponse>('/dashboard/users/signin', userSignin).pipe(map(vetineoApiResponse => {
      console.log(vetineoApiResponse);
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedCopyEver<UserSigninResponse>(new UserSigninResponse(), vetineoApiResponse.response);
      } else {
        return null;
      }
    }));
  }

  /**
   * Pobiera aktualne saldo finansowe użytkownika przypisane do danej kliniki.
   */
  getCurrentBalance(vetcodeId: number): Observable<CurrentBalanceResponse> {
    return this.apiService.get<VetineoApiResponse>(`/users/current-balance/${vetcodeId}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response && vetineoApiResponse.response.balance) {
        console.log(vetineoApiResponse.response);
        return this.commonUtils.bestTypedCopyEver<CurrentBalanceResponse>(new CurrentBalanceResponse(), vetineoApiResponse.response);
      }
    }));
  }

  /**
   * Pobiera pełną historię zamówień złożonych przez danego użytkownika.
   */
  getOrdersHistory(vetcodeId: number): Observable<Order[]> {
    return this.apiService.get<VetineoApiResponse>(`/users/orders-history/${vetcodeId}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response && vetineoApiResponse.response.orders) {
        console.log(vetineoApiResponse.response);
        return this.commonUtils.bestTypedArrayCopyEver<Order, Order[]>(new Order(), [], vetineoApiResponse.response.orders);
      }
    }));
  }

  /**
   * Importuje i przypisuje dane kliniki weterynaryjnej do konta użytkownika,
   * który jest już zarejestrowany.
   *
   * @param uuid Unikalny identyfikator klienta.
   * @param vetcode Unikalny kod identyfikujący lecznicę.
   * @returns Observable emitujący zaimportowany i zaktualizowany obiekt profilu Kliniki.
   */
  importClinicForExistingClient(uuid: string, vetcode: string) {
    return this.apiService.post<VetineoApiResponse>(`/vet-clinics/import/${uuid}/${vetcode}`, {}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<VetClinic>(new VetClinic(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera paginowaną i sortowaną listę użytkowników systemu.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<UsersDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/users/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        const usersResults = new UsersDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new User(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
        return usersResults;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyszukuje użytkowników na podstawie zdefiniowanych filtrów.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<UsersDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/users/search`, data).pipe(take(1), map(vetineoApiResponse => {
      console.log(vetineoApiResponse);
      if (vetineoApiResponse.ok) {
        return new UsersDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new User(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zapisuje zaktualizowane dane profilu użytkownika.
   */
  updateUser(userEdited: User) {
    return this.apiService.put<VetineoApiResponse>(`/dashboard/users`, userEdited).pipe(map(vetineoApiResponse => {
      if (!vetineoApiResponse.errors) {
        return this.commonUtils.bestTypedCopyEver<User>(new User(), vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors?.main);
      }
    }));
  }

  /**
   * Zapisuje zaktualizowane dane pacjenta korzystającego z aplikacji mobilnej.
   */
  updateAppUser(userEdited: User) {
    return this.apiService.put<VetineoApiResponse>(`/dashboard/app-users`, userEdited).pipe(map(vetineoApiResponse => {
      if (!vetineoApiResponse.errors) {
        return this.commonUtils.bestTypedCopyEver<User>(new User(), vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors?.main);
      }
    }));
  }

  /**
   * Ręcznie tworzy konto nowego użytkownika.
   */
  addUser(user: User, sendEmail: boolean) {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/users/new/${sendEmail}`, user).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<User>(new User(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zwraca logi audytowe operacji wykonanych na profilu konkretnego użytkownika.
   */
  showChanges(uuid: string): Observable<Snapshot[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/users/changes/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new Snapshot(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wymusza reset hasła dla konta administracyjnego z poziomu panelu Dashboard.
   */
  resetPasswordForAdmin(userUUID: string) {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/users/user-pass`, {'userUUID': userUUID}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<User>(new User(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wymusza aktywację i modyfikację sesji tokena zabezpieczającego (Pill) dla danego urządzenia.
   */
  setActivePillForUserDevice(userUUID: string, pillType: 'FULL_CLEAR' | 'CAPGO_CLEAR' | 'RESET_PILL') {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/users/activate_pill`, {'userUUID': userUUID, pillType: pillType, expiration: 300}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Sprawdza integralność i status głównego urządzenia przypisanego do użytkownika (Mobile App).
   */
  checkUserActiveDevice(userUUID: string) {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/users/check-active-device`, {'userUUID': userUUID}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Usuwa profil użytkownika.
   */
  deleteUser(uuid: string): Observable<User> {
    return this.apiService.delete<VetineoApiResponse>(`/dashboard/users/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<User>(new User(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Migruje/importuje strukturę kliniki weterynaryjnej powiązanej z danym kontem użytkownika.
   */
  importClinicAndAddUser(userAdded: User) {
    return this.apiService.post<VetineoApiResponse>(`/vet-clinics/import`, userAdded).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<VetClinic>(new VetClinic(), vetineoApiResponse.response);
      }else{

      }
    }));
  }

  /**
   * Pobiera listę użytkowników w ramach danego klienta w celu dystrybucji
   * wiadomości.
   */
  listUsersToSendMsg(vClientUUID: string,search: string): Observable<User[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/users/msg/${vClientUUID}/${search}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new User(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera listę dedykowanych opiekunów handlowych przypisanych do danego klienta.
   */
  listSalesRepresentatives(vClientUUID: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/users/sales/${vClientUUID}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new User(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Inicjuje proces odzyskiwania hasła.
   */
  restoreMyPassword(email: string) {
    return this.apiService.post<VetineoApiResponse>(`/dashboard/users/rp`,{mail:email}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.ok;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Generuje silne, losowe hasło systemowe.
   */
  generatePwd(){
    return this.apiService.get<VetineoApiResponse>('/dashboard/random-pwd').pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return  vetineoApiResponse.response.pwd;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera listę zarejestrowanych zwierząt (Pet) powiązanych z kontem właściciela.
   */
  getAllPetsByUser(uuid: string): Observable<Pet[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/pets/by-user/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok  && vetineoApiResponse.response  && vetineoApiResponse.response.pets) {
        return this.commonUtils.bestTypedArrayCopyEver(new Pet(), [], vetineoApiResponse.response.pets);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera kartotekę pacjenta (Zwierzęcia) bezpośrednio powiązaną z określoną rezerwacją.
   */
  getPetFromVetReservation(vetReservationUUID): Observable<Pet> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/pet/by-vet-reservation/${vetReservationUUID}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok  && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedCopyEver(new Pet(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zwraca listę placówek weterynaryjnych, do których aktualnie zalogowany użytkownik ma przypisane uprawnienia.
   */
  getUserClinics(uuid: string){
    return this.apiService.get<VetineoApiResponse>(`/dashboard/clinics/by-user/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new VetClinic(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }}));
  }

  /**
   * Weryfikuje historię prostej weryfikacji wizyt pacjenta.
   */
  checkUsersVisits(uuid: string){
    return this.apiService.get<VetineoApiResponse>(`/dashboard/user-visits/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new SimpleCheckVisit(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }}));
  }

  /**
   * Sprawdza, czy dla aktualnego kontekstu aktywowany jest moduł płatności internetowych.
   */
  checkIfPaymentsEnabled() {
    return this.apiService.get<VetineoApiResponse>('/dashboard/payments-enabled').pipe(take(1), map(vetineoApiResponse => {
      if(vetineoApiResponse.ok) {
        return vetineoApiResponse;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Sprawdza dostępność modułu Planów Abonamentowych.
   */
  checkCarePlanEnabled() {
    return this.apiService.get<VetineoApiResponse>('/dashboard/care-plan-enabled-check').pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse) {
        return 'OK';
      } else {
        return undefined;
      }}));
  }

  /**
   * Sprawdza, czy aktualny użytkownik posiada uprawnienia do wystawiania rekomendacji produktowych.
   */
  checkUserRecommendationAllowed() {
    return this.apiService.get<VetineoApiResponse>('/dashboard/product-recommendation-allowed').pipe(take(1), map(vetineoApiResponse => {
      if(vetineoApiResponse.ok) {
        return vetineoApiResponse;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Sprawdza poziom uprawnień w kontekście edycji nowych typów wizyt Vetineo.
   */
  checkUserCanEditVetineoVisitTypes() {
    return this.apiService.get<VetineoApiResponse>('/dashboard/vetineo-visit-types-config-allowed').pipe(take(1), map(vetineoApiResponse => {
      if(vetineoApiResponse.ok) {
        return vetineoApiResponse;
      } else {
        return undefined;
      }
    }));
  }


  /**
   * Sprawdza, czy adres e-mail powiązany z kontem znajduje się na czarnej liście.
   */
  checkIfUserEmailIsBlocked(userUUID: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/check-user-email-blocked/${userUUID}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse;
      } else {
        throw new Error(vetineoApiResponse.errors?.main);
      }
    }));
  }

  /**
   * Usuwa adres e-mail z czarnej listy.
   */
  unblockEmail(userUUID: string) {
      return this.apiService.get<VetineoApiResponse>(`/dashboard/unblock-user-email/${userUUID}`).pipe(take(1), map(vetineoApiResponse => {
        if(vetineoApiResponse.ok) {
          return vetineoApiResponse;
        } else {
          throw new Error(vetineoApiResponse.errors?.main);
        }
      }));
    }



  /**
   * Metoda do kopiowania UUID przez użytkowników z call-center
   */
  copyUUID(uuid: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/call-center/copy-user-uuid/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response.uuid;
      } else {
        return undefined;
      }
    }));
  }


  getCallCenterUserSyncCsv(from,to) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/call-center-user-sync-report/${from}/${to}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response.report;
      } else {
        throw Error(vetineoApiResponse.errors.main);
      }
    }));
  }

}
